import axios from "axios";
import { errorHandler } from "@/Services/errorHandler";
import queryStringify from "qs-stringify";

// Sanitize params by removing nullish/empty-string values to avoid sending "undefined" in query
const sanitizeParams = (obj) => {
  try {
    const entries = Object.entries(obj || {}).filter(([_, v]) => v !== undefined && v !== null && v !== '');
    return Object.fromEntries(entries);
  } catch (e) {
    return {};
  }
};

/**
 * WarningsReports Vuex module
 * Implements endpoints from resources/api.yaml under tag WarningsReports
 * All API calls use the global reportsApi prefix; no hardcoded full URLs in components.
 */
export default {
  namespaced: true,
  state: {
    loading: false,
    error: null,
    typesSummary: [],
    storesSummary: [],
    detail: [],
    topStores: [],
    typesLastSeen: [],
    monthlyTrends: [],
    // New: option sources for filters
    warningCategories: [],
    warningTypes: [],
    detailLimit: 100,
    detailOffset: 0,
  },
  getters: {
    loading: (s) => s.loading,
    error: (s) => s.error,
    typesSummary: (s) => s.typesSummary,
    storesSummary: (s) => s.storesSummary,
    detail: (s) => s.detail,
    topStores: (s) => s.topStores,
    typesLastSeen: (s) => s.typesLastSeen,
    monthlyTrends: (s) => s.monthlyTrends,
    // New getters
    warningCategories: (s) => s.warningCategories,
    warningTypes: (s) => s.warningTypes,
    detailLimit: (s) => s.detailLimit,
    detailOffset: (s) => s.detailOffset,
  },
  mutations: {
    SET_LOADING(s, v) { s.loading = v; },
    SET_ERROR(s, e) { s.error = e; },
    SET_TYPES_SUMMARY(s, rows) { s.typesSummary = rows; },
    SET_STORES_SUMMARY(s, rows) { s.storesSummary = rows; },
    SET_DETAIL(s, rows) { s.detail = rows; },
    SET_TOP_STORES(s, rows) { s.topStores = rows; },
    SET_TYPES_LAST_SEEN(s, rows) { s.typesLastSeen = rows; },
    SET_MONTHLY_TRENDS(s, rows) { s.monthlyTrends = rows; },
    // New mutations
    SET_WARNING_CATEGORIES(s, rows) { s.warningCategories = rows || []; },
    SET_WARNING_TYPES(s, rows) { s.warningTypes = rows || []; },
    SET_DETAIL_PAGING(s, { limit, offset }) {
      if (typeof limit === 'number') s.detailLimit = limit;
      if (typeof offset === 'number') s.detailOffset = offset;
    },
  },
  actions: {
    /**
     * Helper to build headers using selected_store and stores in localStorage.
     * Respects project convention of base64-encoding DB/group.
     */
    buildHeaders() {
      const stores = JSON.parse(localStorage.getItem("stores")) || [];
      const store = JSON.parse(localStorage.getItem("selected_store")) || {};
      // Prefer the group from the selected store object; fall back to the first stores entry
      const group = (store && store.Group) ? store.Group : (stores.length ? stores[0].Group : null);
      const storeDB = (store && Object.prototype.hasOwnProperty.call(store, 'StoreDB')) ? store.StoreDB : null;
      const headers = {};
      if (group) headers.group = window.btoa(group);
      if (storeDB !== undefined && storeDB !== null) headers.DB = window.btoa(storeDB);
      return headers;
    },

    /**
     * New: Fetch categories list for filters
     * GET /api/v2/reports/warnings/categories
     */
    async fetchWarningCategories({ commit, dispatch }, params) {
      commit('SET_LOADING', true); commit('SET_ERROR', null);
      try {
        const headers = await dispatch('buildHeaders');
        const queryString = '?' + queryStringify(params || {});
        const { data } = await axios.get(
                  reportsApi + `reports/warnings/categories` + ('?' + queryStringify(sanitizeParams(params || {}))),
                  { headers }
                );
        // Response shape (per spec update): [{ name: string }]
        // Backward-compatible fallbacks: string | { category } | { type }
        const list = Array.isArray(data)
          ? data
              .map(x => {
                if (!x) return null;
                if (typeof x === 'string') return x;
                return x.name || x.category || x.type || null;
              })
              .filter(Boolean)
          : [];
        // Unique + sorted
        const unique = Array.from(new Set(list)).sort((a,b)=>a.localeCompare(b));
        commit('SET_WARNING_CATEGORIES', unique);
        return unique;
      } catch (e) { commit('SET_ERROR', e); return errorHandler.tomcatError(e); }
      finally { commit('SET_LOADING', false); }
    },

    /**
     * New: Fetch types list for filters
     * GET /api/v2/reports/warnings/types
     */
    async fetchWarningTypes({ commit, dispatch }, params) {
      commit('SET_LOADING', true); commit('SET_ERROR', null);
      try {
        const headers = await dispatch('buildHeaders');
        const queryString = '?' + queryStringify(sanitizeParams(params || {}));
        const { data } = await axios.get(reportsApi + `reports/warnings/types` + queryString, { headers });
        // Response shape (per spec update): [{ name: string }]
        // Backward-compatible fallbacks: string | { type } | { category }
        const list = Array.isArray(data)
          ? data
              .map(x => {
                if (!x) return null;
                if (typeof x === 'string') return x;
                return x.name || x.type || x.category || null;
              })
              .filter(Boolean)
          : [];
        const unique = Array.from(new Set(list)).sort((a,b)=>a.localeCompare(b));
        commit('SET_WARNING_TYPES', unique);
        return unique;
      } catch (e) { commit('SET_ERROR', e); return errorHandler.tomcatError(e); }
      finally { commit('SET_LOADING', false); }
    },

    async fetchTypesSummary({ commit, dispatch }, params) {
      commit('SET_LOADING', true); commit('SET_ERROR', null);
      try {
        const headers = await dispatch('buildHeaders');
        const queryString = '?' + queryStringify(sanitizeParams(params || {}));
        const { data } = await axios.get(reportsApi + `reports/warnings/types/summary` + queryString, { headers });
        commit('SET_TYPES_SUMMARY', Array.isArray(data) ? data : []);
        return data;
      } catch (e) { commit('SET_ERROR', e); return errorHandler.tomcatError(e); }
      finally { commit('SET_LOADING', false); }
    },

    async fetchStoresSummary({ commit, dispatch }, params) {
      commit('SET_LOADING', true); commit('SET_ERROR', null);
      try {
        const headers = await dispatch('buildHeaders');
        const queryString = '?' + queryStringify(sanitizeParams(params || {}));
        const { data } = await axios.get(reportsApi + `reports/warnings/stores/summary` + queryString, { headers });
        commit('SET_STORES_SUMMARY', Array.isArray(data) ? data : []);
        return data;
      } catch (e) { commit('SET_ERROR', e); return errorHandler.tomcatError(e); }
      finally { commit('SET_LOADING', false); }
    },

    async fetchDetail({ commit, dispatch }, params) {
      // Updated per spec: detail supports DB (single store) OR group (All stores)
      commit('SET_LOADING', true); commit('SET_ERROR', null);
      try {
        const headers = await dispatch('buildHeaders');
        const queryString = '?' + queryStringify(sanitizeParams(params || {}));
        const { data } = await axios.get(reportsApi + `reports/warnings/detail` + queryString, { headers });
        commit('SET_DETAIL', Array.isArray(data) ? data : []);
        const { limit, offset } = params || {};
        commit('SET_DETAIL_PAGING', { limit, offset });
        return data;
      } catch (e) { commit('SET_ERROR', e); return errorHandler.tomcatError(e); }
      finally { commit('SET_LOADING', false); }
    },

    async fetchTopStores({ commit, dispatch }, params) {
      commit('SET_LOADING', true); commit('SET_ERROR', null);
      try {
        const headers = await dispatch('buildHeaders');
        const queryString = '?' + queryStringify(sanitizeParams(params || {}));
        const { data } = await axios.get(reportsApi + `reports/warnings/stores/top` + queryString, { headers });
        commit('SET_TOP_STORES', Array.isArray(data) ? data : []);
        return data;
      } catch (e) { commit('SET_ERROR', e); return errorHandler.tomcatError(e); }
      finally { commit('SET_LOADING', false); }
    },

    async fetchTypesLastSeen({ commit, dispatch }, params) {
      commit('SET_LOADING', true); commit('SET_ERROR', null);
      try {
        const headers = await dispatch('buildHeaders');
        const queryString = '?' + queryStringify(sanitizeParams(params || {}));
        const { data } = await axios.get(reportsApi + `reports/warnings/types/last-seen` + queryString, { headers });
        commit('SET_TYPES_LAST_SEEN', Array.isArray(data) ? data : []);
        return data;
      } catch (e) { commit('SET_ERROR', e); return errorHandler.tomcatError(e); }
      finally { commit('SET_LOADING', false); }
    },

    async fetchMonthlyTrends({ commit, dispatch }, params) {
      commit('SET_LOADING', true); commit('SET_ERROR', null);
      try {
        const headers = await dispatch('buildHeaders');
        const queryString = '?' + queryStringify(sanitizeParams(params || {}));
        const { data } = await axios.get(reportsApi + `reports/warnings/trends/monthly` + queryString, { headers });
        commit('SET_MONTHLY_TRENDS', Array.isArray(data) ? data : []);
        return data;
      } catch (e) { commit('SET_ERROR', e); return errorHandler.tomcatError(e); }
      finally { commit('SET_LOADING', false); }
    },

    /**
     * Mark a warning as resolved (or un-resolved).
     * POST /api/v2/reports/warnings/{id}/resolve
     * Body: { user: string, resolved: boolean }
     * Optimistically patches the matching row in state.detail on success.
     */
    async resolveWarning({ commit, dispatch }, { id, user, resolved = true }) {
      commit('SET_LOADING', true); commit('SET_ERROR', null);
      try {
        const headers = await dispatch('buildHeaders');
        const { data } = await axios.post(
          reportsApi + `reports/warnings/${id}/resolve`,
          { user, resolved },
          { headers }
        );
        // Patch the local detail row so the modal reflects the new state
        const list = commit && this.state && this.state.detail ? this.state.detail : [];
        const updated = (Array.isArray(list) ? list : []).map(row => {
          if (String(row.id) === String(id)) {
            return {
              ...row,
              resolved: !!data.resolved,
              user: data.resolvedBy || row.user,
              date: data.resolvedAt || row.date,
            };
          }
          return row;
        });
        commit('SET_DETAIL', updated);
        return data;
      } catch (e) { commit('SET_ERROR', e); return errorHandler.tomcatError(e); }
      finally { commit('SET_LOADING', false); }
    },
  },
};
